import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ElementRef,
  inject,
  input,
  computed,
  output,
} from '@angular/core';
import { BehaviorSubject, Subject, interval } from 'rxjs';
import { takeUntil, filter, switchMap } from 'rxjs/operators';
import { SLIDER_ARROW_ICON } from '../../assets/ts-icons/icons';
import { SafePipe } from '../../pipes/safe.pipe';
import { TargetUrl } from '@outlaw/core';

export interface SliderImage {
  imageUrl: string;
  url: string | null;
  alt?: string;
}

@Component({
  selector: 'app-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SafePipe],
})
export class SliderComponent implements OnInit, OnDestroy {
  images = input.required<SliderImage[]>();
  interval = input<number>(10_000);
  openInSameWindow = input<boolean>(false);

  navigateToPost = output<void>();

  currentSlide = 0;

  linkTarget = computed(() => (this.openInSameWindow() ? TargetUrl.sameWindow : TargetUrl.newWindow));

  #cdr = inject(ChangeDetectorRef);
  #elementRef = inject(ElementRef);
  #destroy$ = new Subject<void>();
  #isVisible$ = new BehaviorSubject<boolean>(false);
  #autoplayTimer$ = new Subject<void>();
  isDragging = false;

  protected sliderArrowIcon = SLIDER_ARROW_ICON;

  private touchStartX = 0;
  private touchEndX = 0;
  private minSwipeDistance = 50;

  ngOnInit(): void {
    this.setupIntersectionObserver();
    this.startAutoplay();
    this.setupTouchEvents();
  }

  nextSlide(): void {
    this.currentSlide = (this.currentSlide + 1) % this.images().length;
    this.#cdr.detectChanges();
  }

  goToSlide(index: number): void {
    this.currentSlide = index;
    this.#cdr.detectChanges();
    this.resetAutoplay();
  }

  previousSlide(): void {
    this.currentSlide = this.currentSlide === 0 ? this.images().length - 1 : this.currentSlide - 1;
    this.#cdr.detectChanges();
    this.resetAutoplay();
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
    this.#isVisible$.complete();
    this.#autoplayTimer$.complete();

    const element = this.#elementRef.nativeElement;
    element.removeEventListener('touchstart', () => {});
    element.removeEventListener('touchend', () => {});
  }

  onPostClick(event: Event) {
    if (this.openInSameWindow()) {
      event.preventDefault();
      this.navigateToPost.emit();
    }
  }

  private setupIntersectionObserver(): void {
    const options = {
      rootMargin: '0px',
      threshold: 0.5,
    };

    const observer = new IntersectionObserver(([entry]) => {
      this.#isVisible$.next(entry.isIntersecting);
    }, options);

    observer.observe(this.#elementRef.nativeElement);
  }

  private startAutoplay(): void {
    this.#autoplayTimer$
      .pipe(
        switchMap(() => interval(this.interval()).pipe(filter(() => this.#isVisible$.value))),
        takeUntil(this.#destroy$)
      )
      .subscribe(() => {
        this.nextSlide();
      });

    this.#autoplayTimer$.next();
  }

  private setupTouchEvents(): void {
    const element = this.#elementRef.nativeElement;

    element.addEventListener('dragstart', (e: DragEvent) => {
      e.preventDefault();
    });

    element.addEventListener('touchstart', (e: TouchEvent) => {
      this.touchStartX = e.touches[0].clientX;
    });

    element.addEventListener('touchend', (e: TouchEvent) => {
      this.touchEndX = e.changedTouches[0].clientX;
      this.handleSwipe();
    });

    element.addEventListener('mousedown', (e: MouseEvent) => {
      e.preventDefault();
      this.touchStartX = e.clientX;
      this.isDragging = true;
      this.#cdr.detectChanges();
    });

    element.addEventListener('mouseup', (e: MouseEvent) => {
      this.touchEndX = e.clientX;
      this.isDragging = false;
      this.handleSwipe();
      this.#cdr.detectChanges();
    });

    element.addEventListener('mouseleave', () => {
      this.isDragging = false;
      this.#cdr.detectChanges();
    });
  }

  private handleSwipe(): void {
    const swipeDistance = this.touchEndX - this.touchStartX;

    if (Math.abs(swipeDistance) > this.minSwipeDistance) {
      if (swipeDistance > 0) {
        this.previousSlide();
      } else {
        this.nextSlide();
      }
      this.#autoplayTimer$.next();
    }
  }

  private resetAutoplay(): void {
    if (this.interval() > 0) {
      this.#autoplayTimer$.next();
    }
  }
}
